import { apiClient } from './client';
import { currencyApi } from './currencyApi';

export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';

export interface CoaAccount {
  id: string;
  groupId: string;
  code: string;
  name: string;
  type: AccountType;
  balanceType: 'DEBIT' | 'CREDIT';
  systemTag: string | null;
  openingBalance: number;
  currentBalance: number;
  isActive: boolean;
  isSystem: boolean;
  allowDirectPosting: boolean;
  description: string | null;
  totalDebit?: number;
  totalCredit?: number;
  subParties?: PartySubLedger[];
}

export interface PartySubLedger {
  id: string;
  name: string;
  partyType: string;
  phone?: string;
  pan?: string;
  currentBalance: number;
  balanceType: 'DEBIT' | 'CREDIT';
}

export interface CoaGroup {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  parentId: string | null;
  level: number;
  isSystem: boolean;
  totalBalance: number;
  accounts: CoaAccount[];
  children: CoaGroup[];
}

export interface CoaTreeResponse {
  tree: CoaGroup[];
  groups: CoaGroup[];
  accounts: CoaAccount[];
  summary: {
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
    totalIncome: number;
    totalExpenses: number;
    netProfit: number;
    equationBalanced: boolean;
  };
}

export interface JournalVoucherEntry {
  id?: string;
  accountId: string;
  accountCode?: string;
  accountName?: string;
  accountType?: string;
  partyId?: string | null;
  partyName?: string | null;
  debit: number;
  credit: number;
  narration?: string;
}

export interface JournalVoucher {
  id: string;
  voucherNumber: string;
  voucherDate: string;
  voucherType: string;
  narration: string;
  status: 'DRAFT' | 'POSTED' | 'CANCELLED' | 'REVERSED';
  totalDebit: number;
  totalCredit: number;
  referenceTable?: string | null;
  referenceId?: string | null;
  reversedVoucherId?: string | null;
  isReversed: boolean;
  postedAt?: string | null;
  createdAt: string;
  creatorEmail?: string;
  entriesCount?: number;
  entries?: JournalVoucherEntry[];
}

export interface TrialBalanceRow {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  balanceType: 'DEBIT' | 'CREDIT';
  groupId: string;
  groupCode: string;
  groupName: string;
  openingBalance: number;
  periodDebit: number;
  periodCredit: number;
  closingDebit: number;
  closingCredit: number;
  closingNet: number;
}

export interface TrialBalanceReport {
  asOfDate: string;
  fromDate: string | null;
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
  isBalanced: boolean;
}

export interface ProfitLossItem {
  id: string;
  code: string;
  name: string;
  groupName: string;
  amount: number;
  comparisonAmount?: number;
  changePercent?: number | null;
}

export interface ProfitLossSection {
  items: ProfitLossItem[];
  total: number;
  comparisonTotal?: number;
  changePercent?: number | null;
}

export interface ProfitLossMonthlyItem {
  month: string;
  label: string;
  income: number;
  expenses: number;
  netProfit: number;
  netMarginPercent: number;
}

export interface ProfitLossStatement {
  period: { fromDate: string; toDate: string };
  compare?: {
    mode: string;
    comparisonFrom?: string | null;
    comparisonTo?: string | null;
  };
  summary: {
    totalIncome: number;
    comparisonTotalIncome?: number;
    totalIncomeChangePercent?: number | null;
    totalExpenses: number;
    comparisonTotalExpenses?: number;
    totalExpensesChangePercent?: number | null;
    grossProfit: number;
    comparisonGrossProfit?: number;
    grossProfitChangePercent?: number | null;
    grossMarginPercent: number;
    netProfit: number;
    comparisonNetProfit?: number;
    netProfitChangePercent?: number | null;
    netMarginPercent: number;
    isProfitable: boolean;
  };
  directIncome: ProfitLossSection;
  directExpenses: ProfitLossSection;
  grossProfit: number;
  indirectIncome: ProfitLossSection;
  indirectExpenses: ProfitLossSection;
  totalIncome: number;
  totalExpenses: number;
  netProfit: number;
  monthlyData?: ProfitLossMonthlyItem[];
}

export interface BalanceSheetItem {
  id: string;
  code: string;
  name: string;
  groupName: string;
  amount: number;
  comparisonAmount?: number;
  changePercent?: number | null;
}

export interface BalanceSheetRatios {
  currentRatio: number | null;
  quickRatio: number | null;
  debtToEquity: number | null;
  debtToAssets: number | null;
  netWorth: number;
  workingCapital: number;
  returnOnAssets: number;
}

export interface BalanceSheetStatement {
  asOfDate: string;
  compare?: {
    mode: string;
    comparisonDate?: string | null;
  };
  summary: {
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
    totalLiabilitiesAndEquity: number;
    difference: number;
    isBalanced: boolean;
  };
  ratios?: BalanceSheetRatios;
  assets: {
    currentAssets: { items: BalanceSheetItem[]; total: number };
    fixedAssets: { items: BalanceSheetItem[]; total: number };
    totalAssets: number;
  };
  liabilities: {
    currentLiabilities: { items: BalanceSheetItem[]; total: number };
    longTermLiabilities: { items: BalanceSheetItem[]; total: number };
    totalLiabilities: number;
  };
  equity: {
    items: BalanceSheetItem[];
    totalEquity: number;
  };
  totalLiabilitiesAndEquity: number;
  isBalanced: boolean;
  difference: number;
}

export interface AccountLedgerStatement {
  account: {
    id: string;
    code: string;
    name: string;
    type: AccountType;
    balanceType: 'DEBIT' | 'CREDIT';
    openingBalance: number;
    groupName: string;
  };
  period: { fromDate: string | null; toDate: string | null };
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  entries: {
    id: string;
    entryDate: string;
    debit: number;
    credit: number;
    narration: string;
    voucherId: string;
    voucherNumber: string;
    voucherType: string;
    referenceTable: string | null;
    referenceId: string | null;
    partyName: string | null;
    runningBalance: number;
  }[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PostingRole {
  key: string;
  label: string;
  group: string;
  description: string;
  allowedTypes: AccountType[];
  accountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  source: 'MAPPED' | 'DEFAULT' | 'MISSING';
  defaultAccountId: string | null;
  defaultAccountName: string | null;
}

export interface PostingEvent {
  key: string;
  module: string;
  label: string;
  trigger: string;
  /** role is null when the account is not a posting role (e.g. the asset category's own ledger). */
  lines: { side: 'DR' | 'CR'; role: string | null; account?: string; note: string }[];
}

export interface PostingRules {
  roles: PostingRole[];
  events: PostingEvent[];
}

export interface RebuildGLSummary {
  documents: number;
  posted: number;
  removed: number;
  skipped: number;
  errors: { table: string; id: string; error: string }[];
}

export interface GstSettlementHead {
  head: 'IGST' | 'CGST' | 'SGST';
  output: number;
  input: number;
  creditUsed: number;
  cashPayable: number;
  creditCarriedForward: number;
}

export interface GstSettlementPreview {
  asOfDate: string;
  heads: GstSettlementHead[];
  utilisation: { from: string; to: string; amount: number }[];
  cashPayable: number;
}

export interface FiscalYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  closedAt: string | null;
  closingVoucherId: string | null;
  closingVoucherNumber?: string;
}

/**
 * Source documents whose General Ledger vouchers are generated automatically. Those
 * vouchers follow the document — edit or cancel the document instead of reversing them.
 */
export const DOCUMENT_REFERENCE_TABLES = [
  'parties', 'grns', 'purchase_invoices', 'purchase_payments', 'purchase_returns',
  'production_orders', 'challans', 'sales_invoices', 'sales_payments', 'refunds_given',
  'sale_returns', 'stock_adjustments', 'direct_stock_entries', 'bank_statements',
  'fixed_assets', 'asset_depreciation_runs', 'asset_maintenance',
];

export const isDocumentVoucher = (jv: Pick<JournalVoucher, 'referenceTable'>) =>
  !!jv.referenceTable && DOCUMENT_REFERENCE_TABLES.includes(jv.referenceTable);

export const financeApi = {
  // Chart of Accounts
  getCoaTree: async (params?: { asOfDate?: string }) => {
    const res = await apiClient.get<{ success: boolean; data: CoaTreeResponse }>('/api/v1/finance/tree', { params });
    return res.data.data;
  },

  // Alias for getCoaTree
  getTree: async (params?: { asOfDate?: string }) => {
    const res = await apiClient.get<{ success: boolean; data: CoaTreeResponse }>('/api/v1/finance/tree', { params });
    return res.data.data;
  },

  getAccounts: async (params?: { type?: string; groupId?: string; search?: string; isActive?: boolean }) => {
    const res = await apiClient.get<{ success: boolean; data: CoaAccount[] }>('/api/v1/finance/accounts', { params });
    return res.data.data;
  },

  suggestCode: async (groupId: string) => {
    const res = await apiClient.get<{ success: boolean; data: { nextCode: string } }>('/api/v1/finance/accounts/suggest-code', {
      params: { groupId },
    });
    return res.data.data.nextCode;
  },

  createAccount: async (data: {
    groupId: string;
    code?: string;
    name: string;
    balanceType?: 'DEBIT' | 'CREDIT';
    openingBalance?: number;
    isActive?: boolean;
    allowDirectPosting?: boolean;
    description?: string;
  }) => {
    const res = await apiClient.post<{ success: boolean; data: CoaAccount; message: string }>('/api/v1/finance/accounts', data);
    return res.data.data;
  },

  updateAccount: async (id: string, data: Partial<CoaAccount> & { groupId?: string }) => {
    const res = await apiClient.put<{ success: boolean; data: CoaAccount; message: string }>(`/api/v1/finance/accounts/${id}`, data);
    return res.data.data;
  },

  deleteAccount: async (id: string) => {
    const res = await apiClient.delete<{ success: boolean; message: string }>(`/api/v1/finance/accounts/${id}`);
    return res.data;
  },

  createGroup: async (data: { code: string; name: string; type: AccountType; parentId?: string }) => {
    const res = await apiClient.post<{ success: boolean; data: CoaGroup; message: string }>('/api/v1/finance/groups', data);
    return res.data.data;
  },

  updateGroup: async (id: string, data: { code?: string; name?: string; parentId?: string }) => {
    const res = await apiClient.put<{ success: boolean; data: CoaGroup; message: string }>(`/api/v1/finance/groups/${id}`, data);
    return res.data.data;
  },

  deleteGroup: async (id: string) => {
    const res = await apiClient.delete<{ success: boolean; message: string }>(`/api/v1/finance/groups/${id}`);
    return res.data;
  },

  // Posting Rules (which account each automatic posting uses)
  getPostingRules: async () => {
    const res = await apiClient.get<{ success: boolean; data: PostingRules }>('/api/v1/finance/posting-rules');
    return res.data.data;
  },

  setPostingRule: async (role: string, accountId: string) => {
    const res = await apiClient.put<{ success: boolean; data: PostingRules; message: string }>(`/api/v1/finance/posting-rules/${role}`, { accountId });
    return res.data.data;
  },

  resetPostingRule: async (role: string) => {
    const res = await apiClient.delete<{ success: boolean; data: PostingRules; message: string }>(`/api/v1/finance/posting-rules/${role}`);
    return res.data.data;
  },

  rebuildGL: async () => {
    const res = await apiClient.post<{ success: boolean; data: RebuildGLSummary; message: string }>('/api/v1/finance/rebuild-gl');
    return res.data;
  },

  // GST set-off
  getGstSettlement: async (asOfDate: string) => {
    const res = await apiClient.get<{ success: boolean; data: GstSettlementPreview }>('/api/v1/finance/gst-settlement', { params: { asOfDate } });
    return res.data.data;
  },

  postGstSettlement: async (data: { asOfDate: string; voucherDate?: string; bankAccountId?: string }) => {
    const res = await apiClient.post<{ success: boolean; message: string }>('/api/v1/finance/gst-settlement', data);
    return res.data;
  },

  // Journal Vouchers
  getJournalVouchers: async (params?: { status?: string; fromDate?: string; toDate?: string; search?: string; page?: number; limit?: number }) => {
    const res = await apiClient.get<{
      success: boolean;
      vouchers: JournalVoucher[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>('/api/v1/finance/journal-vouchers', { params });
    return res.data;
  },

  getJournalVoucherById: async (id: string) => {
    const res = await apiClient.get<{ success: boolean; data: JournalVoucher }>(`/api/v1/finance/journal-vouchers/${id}`);
    return res.data.data;
  },

  createJournalVoucher: async (data: {
    voucherDate?: string;
    voucherType?: string;
    narration?: string;
    status?: 'DRAFT' | 'POSTED';
    entries: {
      accountId: string;
      partyId?: string | null;
      debit: number;
      credit: number;
      narration?: string;
    }[];
  }) => {
    const res = await apiClient.post<{ success: boolean; data: JournalVoucher; message: string }>('/api/v1/finance/journal-vouchers', data);
    return res.data.data;
  },

  postJournalVoucher: async (id: string) => {
    const res = await apiClient.post<{ success: boolean; message: string }>(`/api/v1/finance/journal-vouchers/${id}/post`);
    return res.data;
  },

  reverseJournalVoucher: async (id: string, data?: { reversalDate?: string; reason?: string }) => {
    const res = await apiClient.post<{ success: boolean; data: JournalVoucher; message: string }>(`/api/v1/finance/journal-vouchers/${id}/reverse`, data || {});
    return res.data.data;
  },

  // Financial Reports
  getTrialBalance: async (params?: { asOfDate?: string; fromDate?: string }) => {
    const res = await apiClient.get<{ success: boolean; data: TrialBalanceReport }>('/api/v1/finance/reports/trial-balance', { params });
    return res.data.data;
  },

  getProfitLoss: async (params?: { fromDate?: string; toDate?: string; compare?: string; view?: string }) => {
    const res = await apiClient.get<{ success: boolean; data: ProfitLossStatement }>('/api/v1/finance/reports/profit-loss', { params });
    return res.data.data;
  },

  getBalanceSheet: async (params?: { asOfDate?: string; compare?: string }) => {
    const res = await apiClient.get<{ success: boolean; data: BalanceSheetStatement }>('/api/v1/finance/reports/balance-sheet', { params });
    return res.data.data;
  },

  getAccountLedger: async (accountId: string, params?: { fromDate?: string; toDate?: string; page?: number; limit?: number }) => {
    const res = await apiClient.get<{ success: boolean; data: AccountLedgerStatement }>(`/api/v1/finance/reports/ledger/${accountId}`, { params });
    return res.data.data;
  },

  // Fiscal Years
  getFiscalYears: async () => {
    const res = await apiClient.get<{ success: boolean; data: FiscalYear[] }>('/api/v1/finance/fiscal-years');
    return res.data.data;
  },

  closeFiscalYear: async (id: string) => {
    const res = await apiClient.post<{ success: boolean; message: string; netProfit: number }>(`/api/v1/finance/fiscal-years/${id}/close`);
    return res.data;
  },

  // Multi-Currency
  currencies: currencyApi,
};

export { currencyApi, type Currency, type CreateCurrencyPayload, type UpdateCurrencyPayload } from './currencyApi';

